// ED-XXXX Dashboard Form Component
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// import { useOrganizationProvider } from '../../../providers/OrganizationProvider'

import { Box, Input, Switch, FormControlLabel } from '@mui/material'

import { ErrorToast, SuccessToast } from '../../../core/utils/toast-notifications/ToastNotifications'

import { FieldErrors, Resolver, useForm } from 'react-hook-form'

import NotFoundPage from '../../pages/NotFoundPage'
import PageContainer from '../../components/page/PageContainer'
import InnerPageContainer from '../../components/inner-page-container/InnerPageContainer'
import { EdifyFieldLabel, EdifyFormField } from '../../components/form'
import { EdifyButton } from '../../components/buttons'
import { createDashboard, getDashboardById, updatedDashboard } from '../../../domain/domain'
import EdifySearchDropdown, { ISearchResult } from '../../components/form/EdifySearch/EdifySearchDropdown'
import FormErrorText from '../../components/form/FormErrorText'
import { useOrganizationProvider } from '../../../providers/OrganizationProvider'

// Dashboard form input interface with required fields
interface IDashboardInput {
  name: string
  domoLink: string
  embedId: string
}

export const DashboardForm = () => {
  const { id } = useParams()
  const [project, setProject] = useState<ISearchResult | null>(null)
  const [level, setLevel] = useState<ISearchResult | null>(null)
  const [contractor, setContractor] = useState<ISearchResult | null>(null)
  const [favorited, setFavorited] = useState<boolean>(false)
  const [isDefault, setIsDefault] = useState<boolean>(false)
  const [dashboardLoading, setDashboardLoading] = useState<boolean>(false)
  const [dashboardError, setDashboardError] = useState<string | undefined>(undefined)
  const [submissionError, setSubmissionError] = useState<string | undefined>()
  const [submissionLoading, setSubmissionLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const { reloadDashboardLinks } = useOrganizationProvider()

  // Validation
  const resolver: Resolver<IDashboardInput> = async values => {
    const errors: FieldErrors<IDashboardInput> = {}
    if (!values.name) {
      errors.name = {
        type: 'required',
        message: 'Name is required',
      }
    }

    if (!values.embedId) {
      errors.embedId = {
        type: 'required',
        message: 'EmbedId is required',
      }
    }

    return {
      values,
      errors,
    }
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IDashboardInput>({
    resolver,
    mode: 'onChange',
  })

  /**
   * Handles the form submission when the form is valid.
   * Will run resolver validations before submitting
   * @param {Object} data - The form data submitted by the user.
   * @returns {void}
   */
  const onSubmit = handleSubmit(data => {
    const { name, embedId } = data
    if (id) handleUpdate(id, name, embedId)
    else handleCreate(name, embedId)
  })

  const handleCreate = async (name: string, embedId: string) => {
    setSubmissionLoading(true)
    setSubmissionError(undefined)

    const res = await createDashboard({
      name,
      domoLink: `https://public.domo.com/embed/pages/${embedId}`,
      projectId: project?.id || null,
      levelId: level?.id || null,
      contractorId: contractor?.id || null,
      embedId: embedId,
    })

    setSubmissionLoading(false)

    if (res.isLeft()) {
      setSubmissionError(res.left?.message ?? 'An unknown error occurred, please try again later')
      ErrorToast({ title: 'Error adding dashboard' })
      return
    }

    if (res.isRight()) {
      SuccessToast({ title: 'Successfully Created New Dashboard' })
      navigate(-1)
    }
  }

  const handleUpdate = async (id: string, name: string, embedId: string) => {
    setSubmissionLoading(true)
    setSubmissionError(undefined)

    const res = await updatedDashboard(id, {
      name,
      domoLink: `https://public.domo.com/embed/pages/${embedId}`,
      projectId: project?.id || null,
      levelId: level?.id || null,
      contractorId: contractor?.id || null,
      embedId: embedId,
    })

    setSubmissionLoading(false)

    if (res.isLeft()) {
      setSubmissionError(res.left?.message ?? 'An unknown error occurred, please try again later.')
      ErrorToast({ title: 'Error saving new changes.' })
      return
    }

    if (res.isRight()) {
      reloadDashboardLinks()
      SuccessToast({ title: 'Successfully Updated Dashboard.' })
      navigate(-1)
    }
  }

  const projectSelected = (projects: ISearchResult[]) => {
    setProject(projects[0] || null)
  }

  const levelSelected = (levels: ISearchResult[]) => {
    setLevel(levels[0] || null)
  }

  const contractorSelected = (contractors: ISearchResult[]) => {
    setContractor(contractors[0] || null)
  }

  // Get Info for Dashboard Edit
  const getDashboard = async () => {
    setDashboardError(undefined)
    setDashboardLoading(true)

    const res = await getDashboardById(id!)

    setDashboardLoading(false)

    if (res.isLeft() || !res.right) {
      setDashboardError('Error loading dashboard.')
      return
    }

    const dashboard = res.right

    console.log('Dashboard data:', dashboard)

    // Set form values
    reset({
      name: dashboard.name,
      domoLink: dashboard.domoLink,
      embedId: dashboard.embedId,
    })

    // Set other values
    setFavorited(dashboard.isFavorited)
    setIsDefault(dashboard.isDefault)

    // Set related entities
    if (dashboard.projectId) {
      setProject({
        id: dashboard.projectId,
        name: dashboard.project?.name ?? '',
      })
    }

    if (dashboard.levelId) {
      setLevel({
        id: dashboard.levelId,
        name: dashboard.level?.name ?? '',
      })
    }

    if (dashboard.contractorId) {
      setContractor({
        id: dashboard.contractorId,
        name: dashboard.contractor?.name ?? '',
      })
    }
  }

  useEffect(() => {
    if (id) {
      getDashboard()
    }
  }, [id])

  const canEditDashboards = true
  const canCreateDashboards = true

  if (id && !canEditDashboards) return <NotFoundPage />
  if (!id && !canCreateDashboards) return <NotFoundPage />

  return (
    <PageContainer title={id ? 'Edit Dashboard' : 'Create New Dashboard'}>
      <form onSubmit={onSubmit}>
        <InnerPageContainer
          innerPageError={dashboardError}
          innerPageLoading={dashboardLoading}
          innerPageReload={getDashboard}
          sx={{ padding: '24px' }}
          headerTitle='Dashboard Details'
        >
          {submissionError && (
            <InnerPageContainer
              sx={{ padding: '12px', marginTop: '12px' }}
              innerPageError={submissionError}
              close={() => setSubmissionError(undefined)}
            />
          )}

          <EdifyFormField>
            <EdifyFieldLabel required label='DASHBOARD NAME' />
            <Input
              autoFocus
              data-testid='DashboardName'
              sx={{ width: '512px' }}
              disableUnderline
              placeholder='Enter Dashboard Name'
              {...register('name')}
              className={`form-control ${errors?.name ? 'error' : ''}`}
            />
            {errors?.name && <FormErrorText>{errors.name.message}</FormErrorText>}
          </EdifyFormField>

          <EdifyFormField>
            <EdifyFieldLabel required label='Embed Id ' />
            <Input
              data-testid='Embedid'
              sx={{ width: '512px' }}
              disableUnderline
              placeholder='Enter Domo Dashboard EmbedId'
              {...register('embedId')}
              className={`form-control ${errors?.embedId ? 'error' : ''}`}
            />
            {errors?.embedId && <FormErrorText>{errors.embedId.message}</FormErrorText>}
          </EdifyFormField>

          <EdifyFormField>
            <EdifyFieldLabel label='PROJECT' />
            <EdifySearchDropdown
              data-testid='ProjectSearch'
              width={512}
              onSelect={projectSelected}
              initialSelectedItems={project ? [project] : []}
              globalSearchKey='projects'
            />
          </EdifyFormField>

          <EdifyFormField>
            <EdifyFieldLabel label='LEVEL' />
            <EdifySearchDropdown
              data-testid='LevelSearch'
              width={512}
              onSelect={levelSelected}
              initialSelectedItems={level ? [level] : []}
              globalSearchKey='levels'
            />
          </EdifyFormField>

          <EdifyFormField>
            <EdifyFieldLabel label='CONTRACTOR' />
            <EdifySearchDropdown
              data-testid='ContractorSearch'
              width={512}
              onSelect={contractorSelected}
              initialSelectedItems={contractor ? [contractor] : []}
              globalSearchKey='contractors'
            />
          </EdifyFormField>
        </InnerPageContainer>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '20px',
          }}
        >
          <EdifyButton noBackground title='Cancel' onClick={() => navigate(-1)} />
          <Box>
            <EdifyButton
              type='submit'
              disabled={submissionLoading || Object.keys(errors).length > 0}
              data-testid={'SubmitDashboardButton'}
              primary
              title={id ? 'Update Dashboard' : 'Save New Dashboard'}
            />
          </Box>
        </Box>
      </form>
    </PageContainer>
  )
}

export default DashboardForm
